import {z} from 'zod';
import {objectIdSchema, scopeSchema, unsafeJsonRecordKeys} from '../shared';
import {timingConfigSchema} from '../timeline/timing';
import {
  triggerTupleSchema,
  triggerObjectSchema,
  triggerTransforms,
} from './trigger';

export * from './condition';
export * from './scroll';
export * from './scrollUnits';
export * from './trigger';
export * from './control';

export type IX3InteractionVersionedResource = {
  id: InteractionId;
  version: number;
};

export const interactionIdSchema = z.string();
export type InteractionId = z.infer<typeof interactionIdSchema>;

const conditionalPlaybackBehaviorSchema = z.enum([
  'dont-animate',
  'skip-to-end',
]);
export type ConditionalPlaybackBehavior = z.infer<
  typeof conditionalPlaybackBehaviorSchema
>;

const prefersReducedMotionPlaybackSchema = z.object({
  type: z.literal('prefers-reduced-motion'),
  behavior: conditionalPlaybackBehaviorSchema,
});

const breakpointPlaybackSchema = z.object({
  type: z.literal('breakpoint'),
  breakpoints: z.array(z.enum(['main', 'medium', 'small', 'tiny'])),
  behavior: conditionalPlaybackBehaviorSchema,
});

export const conditionalPlaybackSchema = z.array(
  z.discriminatedUnion('type', [
    prefersReducedMotionPlaybackSchema,
    breakpointPlaybackSchema,
  ])
);
export type ConditionalPlayback = z.infer<typeof conditionalPlaybackSchema>;
export type ConditionalPlaybackItem = ConditionalPlayback[0];

/**
 * The component settings a component interaction was last reconciled against.
 *
 * Written by the Designer, read only by the Designer — the server never derives
 * anything from it. It is stored so the comparison survives the session: a
 * seeded interaction follows its component's settings, and telling "the author
 * deleted this row on purpose" from "the setting moved while nobody was
 * watching" needs a baseline from before the change. Held in Designer memory it
 * would be recorded fresh every session, so a change made through the Data API,
 * the MCP server, an agent, or multiplayer with this page unloaded would never
 * be a transition and would never reconcile.
 *
 * `contexts` is positional with the instances the interaction drives, in trigger
 * order, and `setKey` names that set. A baseline read against a DIFFERENT set is
 * not one a removal can be keyed on, so a mismatch re-baselines rather than
 * diffing — which is also what makes this safe across a snapshot restore, where
 * ids are remapped and no `setKey` written here can still match.
 *
 * Values are primitives because every seeder's context is a flat record of them;
 * that contract is what makes the baseline storable and shallow-comparable.
 *
 * No size bounds here, unlike `assignedInstances`, and that is deliberate: the
 * two sanitizers named below are the only place the caps are enforced, and they
 * clamp rather than reject. A baseline cut short only makes the Designer
 * re-baseline, whereas a `.max()` here would fail the write of the whole
 * interaction.
 */
export const componentSeedSchema = z.object({
  setKey: z.string(),
  contexts: z.array(
    z.record(
      z.string(),
      z.union([z.string(), z.number(), z.boolean(), z.null()])
    )
  ),
});
export type ComponentSeed = z.infer<typeof componentSeedSchema>;

// Storage caps enforced by both componentSeed sanitizers: the HTTP write path
// (`entrypoints/server/domains/ix3/logic/security.ts`) and the multiplayer
// validator (`entrypoints/multiplayer-server/src/logic/validateAndUpdateIX3Store.ts`).
// Shared rather than mirrored because the two must agree for a baseline to
// round-trip identically between the replicated store and the persisted
// document: a validator clamping harder than persistence would leave
// collaborators holding a shorter setKey, which reads as a different set and
// re-baselines on every load.

/** Cap on the instances one component interaction's baseline may describe. */
export const MAX_COMPONENT_SEED_CONTEXTS = 32;
/** Cap on the settings one instance's context may carry. */
export const MAX_COMPONENT_SEED_KEYS = 16;
// Independent of MAX_COMPONENT_SEED_CONTEXTS: a setKey packs up to 32
// `instPathKey` pairs. Sized to hold the full worst case (32 pairs of two
// 36-char ids, 73 chars each plus a separator, ~2.4k) so ordinary authoring
// can never clip one. A clipped setKey is not corruption — the Designer reads
// a mismatched key as "different set" and re-baselines — but the client keeps
// writing the full key, so every save of that interaction would re-normalize
// and raise the `normalized` sanitizer warning and metric, which has to keep
// meaning "something unexpected wrote this".
export const MAX_COMPONENT_SEED_SET_KEY_LENGTH = 4096;
/** Cap on one setting key's length. */
export const MAX_COMPONENT_SEED_KEY_LENGTH = 64;
export const MAX_COMPONENT_SEED_VALUE_LENGTH = 256;

/**
 * The context keys both componentSeed sanitizers keep. Shared for the same
 * reason as the caps above: the two keeping different key sets leaves the
 * Designer reading a mismatched baseline, which it re-baselines on every load.
 *
 * Every rejected key is dropped rather than rewritten, since rewriting can
 * collide two distinct keys into one stored key with the last value silently
 * winning:
 * - markup or leading/trailing whitespace — exactly what the HTTP path's
 *   `sanitizeString` escaping changes (js-xss's escaper only touches `<` and
 *   `>`, and the result is trimmed), which that path still asserts on its own;
 * - an over-length key, which truncating would collide the same way;
 * - a Mongo-unsafe (`.`, a null byte, leading `$`) or prototype-polluting key.
 */
export const isStorableComponentSeedKey = (key: string): boolean =>
  key !== '' &&
  !/[<>]/.test(key) &&
  key.trim() === key &&
  key.length <= MAX_COMPONENT_SEED_KEY_LENGTH &&
  !key.includes('.') &&
  !key.includes('\0') &&
  key[0] !== '$' &&
  !unsafeJsonRecordKeys.has(key);

// componentSeed's own fields, which both sanitizers rebuild the seed from.
// Typed against `ComponentSeed`, so a field added to `componentSeedSchema`
// fails to compile here until it is listed — the cue to make both rebuilds
// emit it rather than silently drop it.
const COMPONENT_SEED_FIELDS: Readonly<Record<keyof ComponentSeed, true>> = {
  setKey: true,
  contexts: true,
};

/** Whether a componentSeed has an own key `componentSeedSchema` doesn't define. */
export const hasUnknownComponentSeedFields = (seed: object): boolean =>
  Object.keys(seed).some(
    (key) => !Object.prototype.hasOwnProperty.call(COMPONENT_SEED_FIELDS, key)
  );

/** Common fields shared between frontend and backend representations of an interaction. */
const baseInteraction = {
  /** version used for realtime */
  version: z.number().int().nonnegative().optional(),
  /** Page ID that this interaction belongs to */
  pageId: objectIdSchema,
  /** Whether this interaction has been deleted */
  deleted: z.boolean().default(false),
  /** Human-friendly name for display/organization */
  name: z.string(),
  /** Scope of the interaction (site or specific pages) */
  scope: scopeSchema.default({type: 'site'}),
  /** Ordered sequence of timeline IDs to execute */
  timelineIds: z.array(z.string()),
  /** Default timing configuration for all timelines for this interaction */
  timelineDefaults: timingConfigSchema.optional(),
  /** Conditional playback configurations */
  conditionalPlayback: conditionalPlaybackSchema.nullable().optional(),
  /** Settings baseline for a component interaction; see `componentSeedSchema`. */
  componentSeed: componentSeedSchema.optional(),
} as const;

/** The interaction shape used in the client (ie designer).
 * This is the type of each element in UpdateInteractionParams.
 * Differs from the server version in that triggers are stored as tuples. */
export const interactionClientSchema = z.object({
  id: interactionIdSchema,
  ...baseInteraction,
  /** Triggers that initiate this interaction */
  triggers: z.array(triggerTupleSchema),
});

/** We define the frontend params here and create an explicit zod array to
 * help us parse/validate in the request */
export type InteractionClient = z.infer<typeof interactionClientSchema>;
export type Interaction = InteractionClient;
export const clientInteractionsSchema = z.array(interactionClientSchema);
export type ClientInteractions = z.infer<typeof clientInteractionsSchema>;

/** Backend schema used for persistence (converted from InteractionClient).
 * Triggers are stored as objects instead of tuples. */
export const interactionServerSchema = z.object({
  interactionId: interactionIdSchema,
  ...baseInteraction,
  /** Triggers that initiate this interaction (object) */
  triggers: z.array(triggerObjectSchema),
});

/** Persistence types for interaction updates.
 * UpdateInteractionDatum is a single element in the UpdateInteractionData array. */
export type UpdateInteractionDatum = z.infer<typeof interactionServerSchema>;
export const interactionData = z.array(interactionServerSchema);
export type UpdateInteractionData = z.infer<typeof interactionData>;

/** Represents the full interaction document in Mongo, including metadata.
 * This is returned to the client on read/write operations. */
export const rawInteractionSchema = interactionClientSchema.extend({
  createdOn: z.union([z.date(), z.null()]).optional(),
  updatedOn: z.union([z.date(), z.null()]).optional(),
  siteId: objectIdSchema,
});
export type RawInteraction = z.infer<typeof rawInteractionSchema>;

/** Utility to convert interactions between client and server formats (tuple/object triggers). */
export const interactionTransforms = {
  clientToServer: ({
    id,
    triggers,
    ...client
  }: InteractionClient): UpdateInteractionDatum => {
    return {
      ...client,
      interactionId: id,
      triggers: triggers.map((t) => triggerTransforms.tupleToObject(t)),
    };
  },

  rawInteractionToClient: (
    rawInteraction: RawInteraction
  ): InteractionClient => {
    return {
      id: rawInteraction.id,
      version: rawInteraction.version,
      name: rawInteraction.name,
      pageId: rawInteraction.pageId,
      scope: rawInteraction.scope,
      triggers: rawInteraction.triggers,
      timelineIds: rawInteraction.timelineIds,
      timelineDefaults: rawInteraction.timelineDefaults,
      conditionalPlayback: rawInteraction.conditionalPlayback,
      deleted: rawInteraction.deleted,
      componentSeed: rawInteraction.componentSeed,
    };
  },

  /** Converts a raw interaction to an id/version resource */
  rawInteractionToVersionedResource: (
    rawInteraction: RawInteraction
  ): IX3InteractionVersionedResource => {
    return {
      id: rawInteraction.id,
      // Default to 0 (indicating no version exists in the db yet) if the version is missing
      version: rawInteraction.version || 0,
    };
  },

  /**
   * Converts a client interaction to a raw server interaction object. Note: since the client
   * Interaction doesn't contain created/updated dates, we drop them from the Raw interaction as well. This is safer
   * and more accurate than creating placeholder Date objects with the current timestamp.
   */
  clientToRawInteraction: (
    clientInteraction: InteractionClient,
    siteId: string
  ): RawInteraction => {
    return {
      ...clientInteraction,
      siteId,
    };
  },
};

/** These types will assist us for publishing and scoping interactions per-page.*/
export type IX3InteractionsMap = {
  site: InteractionClient[];
  [pageId: string]: InteractionClient[];
};

export type IX3InteractionsServerMap = {
  site: UpdateInteractionDatum[];
  [pageId: string]: UpdateInteractionDatum[];
};
